import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI, inventoryAPI } from '../services/api';
import SucursalChips from './SucursalChips';
import './DashboardBotica.css';

/* ─── Helpers ─────────────────────────────────────────────── */
const fmt = (v) =>
    new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(v ?? 0);

/**
 * Devuelve los días que faltan hasta una fecha.
 * Negativo = ya venció.
 */
function diasHastaVencimiento(fechaStr) {
    if (!fechaStr) return null;
    const hoy  = new Date();
    const venc = new Date(fechaStr);
    hoy.setHours(0, 0, 0, 0);
    venc.setHours(0, 0, 0, 0);
    return Math.round((venc - hoy) / (1000 * 60 * 60 * 24));
}

/**
 * Clasifica el semáforo según días restantes.
 */
function claseSemaforo(dias) {
    if (dias === null) return 'verde';
    if (dias < 0)   return 'rojo';    // vencido
    if (dias <= 30) return 'rojo';    // ≤ 30 días → crítico
    if (dias <= 90) return 'naranja'; // ≤ 90 días → alerta
    return 'verde';
}

function etiquetaDias(dias) {
    if (dias === null) return '—';
    if (dias < 0)  return `Vencido hace ${Math.abs(dias)}d`;
    if (dias === 0) return 'Vence hoy';
    return `${dias}d`;
}

/* ─── Sub-componente: Tarjeta de estadística ──────────────── */
const StatCard = ({ icon, label, value, sub, subClass = '', badge, badgeClass = 'ok', variant = '' }) => (
    <div className={`botica-stat-card ${variant}`}>
        <div className="botica-stat-header">
            <span className="botica-stat-icon">{icon}</span>
            {badge !== undefined && (
                <span className={`botica-stat-badge ${badgeClass}`}>{badge}</span>
            )}
        </div>
        <div className="botica-stat-value">{value}</div>
        <div className="botica-stat-label">{label}</div>
        {sub && <div className={`botica-stat-sub ${subClass}`}>{sub}</div>}
    </div>
);

/* ═══════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════════════════ */
const DashboardBotica = () => {
    const [stats,      setStats]      = useState(null);
    const [lotes,      setLotes]      = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [error,      setError]      = useState(null);
    const [period,     setPeriod]     = useState('today');
    const [sucursalFiltro, setSucursalFiltro] = useState(null);

    /* ── Carga de datos ──────────────────────────────────── */
    const loadAll = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = { period };
            if (sucursalFiltro) params.sucursal_id = sucursalFiltro;

            const [statsRes, lotesRes] = await Promise.all([
                dashboardAPI.getStats(params),
                inventoryAPI.getAlertasLotes(sucursalFiltro ? { sucursal_id: sucursalFiltro } : {}),
            ]);

            setStats(statsRes.data.data);

            // Ordenar lotes: vencidos primero, luego por fecha ascendente
            const rawLotes = lotesRes.data?.data?.lotes || [];
            const sorted = [...rawLotes].sort((a, b) => {
                const dA = diasHastaVencimiento(a.fecha_vencimiento) ?? 9999;
                const dB = diasHastaVencimiento(b.fecha_vencimiento) ?? 9999;
                return dA - dB;
            });
            setLotes(sorted);
        } catch (err) {
            console.error('DashboardBotica error:', err);
            setError(err.response?.data?.message || 'Error al cargar el dashboard');
        } finally {
            setLoading(false);
        }
    }, [period, sucursalFiltro]);

    useEffect(() => { loadAll(); }, [loadAll]);

    /* ── Loading / Error ─────────────────────────────────── */
    if (loading) {
        return (
            <div className="botica-loading">
                <div className="botica-spinner" />
                <span>Cargando dashboard de farmacia…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="botica-error">
                <span style={{ fontSize: 36 }}>⚠️</span>
                <p>{error}</p>
                <button className="botica-refresh-btn" onClick={loadAll}>🔄 Reintentar</button>
            </div>
        );
    }

    /* ── Métricas derivadas de lotes ─────────────────────── */
    const lotesVencidos   = lotes.filter(l => (diasHastaVencimiento(l.fecha_vencimiento) ?? 1) < 0);
    const lotesCriticos   = lotes.filter(l => { const d = diasHastaVencimiento(l.fecha_vencimiento); return d !== null && d >= 0 && d <= 30; });
    const lotesAlerta     = lotes.filter(l => { const d = diasHastaVencimiento(l.fecha_vencimiento); return d !== null && d > 30 && d <= 90; });
    const lotesMostrados  = lotes.slice(0, 15); // tabla: solo los primeros 15

    /* ── Derivados financieros ───────────────────────────── */
    const s = stats || {};
    const today  = s.today  || {};
    const periodo = s.period || {};
    const inv    = s.inventory || {};
    const topProds = s.topProducts || [];
    const salesChart = s.salesChart || [];

    const maxChart = Math.max(...salesChart.map(d => parseFloat(d.total_amount || 0)), 1);

    const periodLabel = {
        today: 'Hoy', week: 'Esta semana', month: 'Este mes', year: 'Este año'
    }[period] || 'Hoy';

    /* ── Render ──────────────────────────────────────────── */
    return (
        <div className="botica-dashboard">

            {/* ── Header ──────────────────────────────────── */}
            <div className="botica-header">
                <div className="botica-header-title">
                    <div className="botica-header-icon">💊</div>
                    <div>
                        <h1>Dashboard Farmacia</h1>
                        <p>Panel de control especializado para botica / farmacia</p>
                    </div>
                </div>
                <div className="botica-header-actions">
                    <div className="botica-period-filters">
                        {['today','week','month','year'].map(p => (
                            <button
                                key={p}
                                className={`botica-period-btn ${period === p ? 'active' : ''}`}
                                onClick={() => setPeriod(p)}
                            >
                                {p === 'today' ? 'Hoy' : p === 'week' ? 'Semana' : p === 'month' ? 'Mes' : 'Año'}
                            </button>
                        ))}
                    </div>
                    <button className="botica-refresh-btn" onClick={loadAll}>🔄</button>
                </div>
            </div>

            {/* ── Filtro de Sucursal ───────────────────────── */}
            <SucursalChips sucursalActiva={sucursalFiltro} onChange={setSucursalFiltro} />

            {/* ── Tarjetas de Alertas de Lotes ────────────── */}
            <div className="botica-stats-grid">
                <StatCard
                    icon="🚨"
                    label="Lotes Vencidos"
                    value={lotesVencidos.length}
                    sub={lotesVencidos.length > 0 ? 'Requieren baja inmediata' : 'Sin vencidos ✅'}
                    subClass={lotesVencidos.length > 0 ? 'red' : ''}
                    badge={lotesVencidos.length > 0 ? 'CRÍTICO' : 'OK'}
                    badgeClass={lotesVencidos.length > 0 ? 'danger' : 'ok'}
                    variant={lotesVencidos.length > 0 ? 'danger' : ''}
                />
                <StatCard
                    icon="⚠️"
                    label="Vencen en ≤ 30 días"
                    value={lotesCriticos.length}
                    sub={lotesCriticos.length > 0 ? 'Dar salida prioritaria' : 'Sin críticos ✅'}
                    subClass={lotesCriticos.length > 0 ? 'red' : ''}
                    badge={lotesCriticos.length > 0 ? 'URGENTE' : 'OK'}
                    badgeClass={lotesCriticos.length > 0 ? 'danger' : 'ok'}
                    variant={lotesCriticos.length > 0 ? 'warning' : ''}
                />
                <StatCard
                    icon="🕐"
                    label="Vencen en 30–90 días"
                    value={lotesAlerta.length}
                    sub="Monitorear rotación"
                    subClass={lotesAlerta.length > 0 ? 'amber' : ''}
                    badge={lotesAlerta.length > 0 ? 'ALERTA' : 'OK'}
                    badgeClass={lotesAlerta.length > 0 ? 'warn' : 'ok'}
                />
                <StatCard
                    icon="📦"
                    label="Stock Bajo"
                    value={inv.lowStock ?? 0}
                    sub={inv.outOfStock > 0 ? `🚫 Sin stock: ${inv.outOfStock}` : 'Sin agotados ✅'}
                    subClass={inv.outOfStock > 0 ? 'red' : ''}
                    badge={inv.outOfStock > 0 ? 'AGOTADOS' : 'OK'}
                    badgeClass={inv.outOfStock > 0 ? 'danger' : 'ok'}
                    variant={inv.outOfStock > 0 ? 'danger' : ''}
                />
            </div>

            {/* ── Tarjetas Financieras ─────────────────────── */}
            <div className="botica-stats-grid" style={{ marginBottom: 28 }}>
                <StatCard
                    icon="💰"
                    label={`Ventas — ${periodLabel}`}
                    value={periodo.sales ?? 0}
                    sub={fmt(periodo.revenue)}
                />
                <StatCard
                    icon="📈"
                    label="Ingresos del período"
                    value={fmt(periodo.revenue)}
                    sub={`Ganancia neta: ${fmt(periodo.profit)}`}
                    subClass={periodo.profit >= 0 ? '' : 'red'}
                />
                <StatCard
                    icon="📉"
                    label="Gastos del período"
                    value={fmt(periodo.expenses)}
                    sub="Total de egresos registrados"
                />
                <StatCard
                    icon="👥"
                    label="Clientes registrados"
                    value={s.customers?.total ?? 0}
                    sub="Base de clientes activa"
                />
            </div>

            {/* ── Sección: Gráfica + Top Medicamentos ─────── */}
            <div className="botica-grid-2">

                {/* Gráfica de ventas */}
                <div className="botica-panel">
                    <div className="botica-panel-header">
                        <h3>📊 Ventas — {periodLabel}</h3>
                    </div>
                    <div className="botica-panel-body">
                        {salesChart.length > 0 ? (
                            <div className="botica-sales-chart">
                                {salesChart.map((day, i) => {
                                    const pct = (parseFloat(day.total_amount) / maxChart) * 100;
                                    const label = new Date(day.date + 'T00:00:00').toLocaleDateString('es-PE', { day: '2-digit', month: 'short' });
                                    return (
                                        <div key={i} className="botica-sales-bar-wrap" title={`${label}: ${fmt(day.total_amount)}`}>
                                            <div
                                                className="botica-sales-bar"
                                                style={{ height: `${Math.max(pct, 5)}%` }}
                                            />
                                            <div className="botica-sales-bar-label">{label}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="botica-empty">
                                <div className="botica-empty-icon">📭</div>
                                <p>Sin ventas en este período</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Top 5 Medicamentos */}
                <div className="botica-panel">
                    <div className="botica-panel-header">
                        <h3>🏆 Top 5 Medicamentos</h3>
                    </div>
                    <div className="botica-panel-body">
                        {topProds.length > 0 ? (
                            <div className="botica-bar-list">
                                {topProds.slice(0, 5).map((p, i) => {
                                    const maxVal = parseFloat(topProds[0]?.total_sales || 1);
                                    const pct = (parseFloat(p.total_sales) / maxVal) * 100;
                                    return (
                                        <div key={i} className="botica-bar-item">
                                            <div className="botica-bar-label-row">
                                                <span>
                                                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i+1}.`}{' '}
                                                    {p.product_name}
                                                </span>
                                                <span className="botica-bar-amount">
                                                    {p.times_sold} uds — {fmt(p.total_sales)}
                                                </span>
                                            </div>
                                            <div className="botica-bar-track">
                                                <div
                                                    className={`botica-bar-fill rank-${Math.min(i, 2)}`}
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="botica-empty">
                                <div className="botica-empty-icon">💊</div>
                                <p>Sin ventas en este período</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Tabla de Lotes con semáforo ──────────────── */}
            <div className="botica-panel-full">
                <div className="botica-panel-header">
                    <h3>🧪 Lotes por Vencer — Semáforo</h3>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>
                        Mostrando {lotesMostrados.length} de {lotes.length} lotes con alertas
                    </span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    {lotesMostrados.length > 0 ? (
                        <table className="botica-lotes-table">
                            <thead>
                                <tr>
                                    <th>Medicamento</th>
                                    <th>Lote</th>
                                    <th>Stock</th>
                                    <th>Vencimiento</th>
                                    <th>Días restantes</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lotesMostrados.map((lote, i) => {
                                    const dias   = diasHastaVencimiento(lote.fecha_vencimiento);
                                    const clase  = claseSemaforo(dias);
                                    const emoji  = clase === 'rojo' ? '🔴' : clase === 'naranja' ? '🟡' : '🟢';
                                    return (
                                        <tr key={i}>
                                            <td style={{ fontWeight: 600 }}>{lote.product_name || lote.nombre_producto || '—'}</td>
                                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{lote.numero_lote || '—'}</td>
                                            <td style={{ fontWeight: 700, color: lote.stock_disponible <= 0 ? '#ef4444' : '#111' }}>
                                                {lote.stock_disponible ?? '—'}
                                            </td>
                                            <td>
                                                {lote.fecha_vencimiento
                                                    ? new Date(lote.fecha_vencimiento).toLocaleDateString('es-PE')
                                                    : '—'}
                                            </td>
                                            <td>
                                                <span className={`venc-pill ${clase}`}>
                                                    {emoji} {etiquetaDias(dias)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`venc-pill ${clase}`}>
                                                    {clase === 'rojo' && dias < 0  ? 'VENCIDO'   : ''}
                                                    {clase === 'rojo' && dias >= 0 ? 'CRÍTICO'  : ''}
                                                    {clase === 'naranja'           ? 'ALERTA'   : ''}
                                                    {clase === 'verde'             ? 'OK'       : ''}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div className="botica-empty">
                            <div className="botica-empty-icon">✅</div>
                            <p>Sin lotes con alertas de vencimiento. ¡Todo en orden!</p>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default DashboardBotica;
